// What Today says about a strategy that printed nothing — and the one line it
// says about the whole board on a day nothing beats the market.
//
// Both used to live inside TodayPage.vue, which was fine while the screen was
// the only thing that said them. It is not: the share card prints every
// section of Today, blank ones included, so the picture and the screen have to
// reach for the same sentence or they will disagree about the same quiet
// morning. One module, imported by both.
//
// It reads the store and never writes to it.

import { sportsInUniverse } from '../../store.js';
import { coveredSportsFor, statusesFor } from './models.js';
import { modelNote, noLegNote } from '../strategy-copy.js';
import { NO_RACING_CARD } from '../engine/strategies/racing.js';
// Two more notes already written in the punter's voice: a model that has not
// earned a price yet, and a day whose exposure budget is spent (engine review
// #12 and #13).
import { BUDGET_SPENT, MODEL_UNPROVEN } from '../engine/strategy.js';

/**
 * The day's answer, above the ranked run of sections rather than at the top of
 * the screen: Classic prints whatever the board gives it and is not in the
 * ranking's order, so the line sits where the ranking starts.
 */
export const VERDICT_LINE = 'Nothing today beats the market’s price. The tickets below are the least bad: if you bet, you are paying the margin.';

/**
 * Why a strategy printed nothing, in the pad's voice rather than the engine's.
 *
 * Worked out here rather than in the section, because the answer depends on
 * the model store and on which sports are on today's board — state a component
 * that only lays out tickets has no business reaching for. A section with
 * tickets on it is asked nothing: `statusesFor` walks every sport on the board
 * and there is no question to answer.
 *
 * @param {object} group `{ strategy, tickets, notes, candidates }`
 * @returns {string} one sentence, or '' when the section dealt something
 */
export function blankNoteFor(group) {
  if (group?.tickets?.length) return '';
  const notes = group?.notes ?? [];
  // THE ONE NOTE ALREADY IN THE PUNTER'S VOICE. Everything else here is the
  // engine's account of itself being translated; this one is a racing strategy
  // saying there is no card on the device yet, which is not a fact about the
  // board, the model store or the edge test — it is an instruction, and the
  // store is where it is written (`engine/strategies/racing.js`).
  if (notes.includes(NO_RACING_CARD)) return NO_RACING_CARD;
  // THE MODEL HAS NOT EARNED A PRICE (engine review #12). Same kind of sentence
  // as the racing card's: not a fact about today's board, and it will read the
  // same tomorrow, so it is printed as it was written.
  if (notes.includes(MODEL_UNPROVEN)) return MODEL_UNPROVEN;
  // The day's money is gone, which is a fact about the DAY and not about this
  // strategy. A section that found legs and could not fund them says so here
  // rather than printing a ticket nobody can back.
  if (notes.includes(BUDGET_SPENT)) return BUDGET_SPENT;
  if (notes.some((n) => n.startsWith('no model supplied'))) {
    // The engine is reporting a missing argument. What the reader needs to
    // know is whether it is still on its way or was never published — which
    // only the model store knows — and WHICH SPORTS either answer is about,
    // which is the board and the model's own coverage, both of them here.
    return modelNote(
      statusesFor(group.strategy, sportsInUniverse.value),
      group.strategy?.probabilitySource?.model,
      {
        name: group.strategy?.name,
        covered: coveredSportsFor(group.strategy),
        board: sportsInUniverse.value,
      },
    );
  }
  if (notes.some((n) => n.startsWith('invalid strategy'))) {
    return 'This strategy needs fixing before it can print.';
  }
  if (notes.some((n) => n.includes('legs available'))) {
    return 'Some legs cleared the edge test, but not enough to fill a ticket.';
  }
  return noLegNote(group?.candidates);
}
